// MARK://dhk8/admin-shell/pages/modules/ModifierDetailPage
// Ficha "ver-primero" de un GRUPO de modificadores (barrido fila→ficha, Gustavo
// 2026-06-23). Header con nombre + resumen + pills (estado / obligatorio) + KPIs
// (selección, # opciones, # productos vinculados) + Tabs (General, Opciones,
// Historial). El tab General se ve primero; "Editar" abre el SectionedSheet con el
// MISMO formulario que el modal de la lista. No existe getModifierGroup(id) en el
// client, así que cargamos la lista y find(id) (igual que ProductDetailPage hace con
// las recetas). Las opciones (add/edit/remove/toggle) y el vínculo a productos se
// gestionan aquí; "Nuevo grupo" se queda como sheet en la lista.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Plus, Trash2, Power, Search, Link2 } from 'lucide-react'

import * as mod from '../../lib/clients/modifiers.js'
import * as cat from '../../lib/clients/catalog.js'
import type { Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn, parseMxnToCents } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'opciones' | 'historial'

interface GroupForm {
  name: string
  minSelect: number
  maxSelect: number
  isRequired: boolean
}

interface OptionForm {
  id: string | null // null = creating
  label: string
  priceDeltaCents: number
  sortOrder: number
}

const BLANK_OPTION: OptionForm = { id: null, label: '', priceDeltaCents: 0, sortOrder: 0 }

export default function ModifierDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()

  // No hay getModifierGroup(id) en el client → traemos la lista y find(id).
  const groups = useAsync((s) => mod.listModifierGroups({}, s), [id])
  const products = useAsync((s) => cat.listProducts(s))

  const [tab, setTab] = useState<Tab>('general')
  const [busyId, setBusyId] = useState<string | null>(null)

  // edición del grupo (mismo form que el modal de la lista)
  const [editing, setEditing] = useState(false)
  const [groupForm, setGroupForm] = useState<GroupForm>({ name: '', minSelect: 0, maxSelect: 1, isRequired: false })
  const [groupBusy, setGroupBusy] = useState(false)

  // crear/editar opción (mismo sheet que la lista)
  const [optionDialog, setOptionDialog] = useState<OptionForm | null>(null)
  const [optionBusy, setOptionBusy] = useState(false)

  // vincular productos (mismo sheet multi-select que la lista)
  const [linking, setLinking] = useState(false)
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set())
  const [linkBusy, setLinkBusy] = useState(false)
  const [productSearch, setProductSearch] = useState('')

  const g = useMemo(() => (groups.data?.items ?? []).find((x) => x.id === id) ?? null, [groups.data, id])
  // Resuelve nombre del producto vinculado para el tab General.
  const productName = (pid: string) => products.data?.find((p) => p.id === pid)?.name ?? pid

  // ── group edit ────────────────────────────────────────────────────────────
  function openEdit() {
    if (!g) return
    setGroupForm({ name: g.name, minSelect: g.minSelect, maxSelect: g.maxSelect, isRequired: g.isRequired })
    setEditing(true)
  }
  async function saveGroup() {
    if (!g || !groupForm.name.trim()) return
    setGroupBusy(true)
    try {
      await mod.updateModifierGroup(g.id, {
        name: groupForm.name.trim(),
        minSelect: groupForm.minSelect,
        maxSelect: groupForm.maxSelect,
        isRequired: groupForm.isRequired,
      })
      toast('Grupo actualizado', 'success')
      setEditing(false)
      groups.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo guardar el grupo.', 'danger')
    } finally {
      setGroupBusy(false)
    }
  }
  async function toggleGroupActive() {
    if (!g) return
    setBusyId(g.id)
    try {
      await mod.updateModifierGroup(g.id, { active: !g.active })
      toast(g.active ? 'Grupo desactivado' : 'Grupo activado', 'success')
      groups.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo cambiar el estado.', 'danger')
    } finally { setBusyId(null) }
  }
  async function removeGroup() {
    if (!g) return
    const ok = await confirm({
      title: `¿Eliminar el grupo "${g.name}"?`,
      message: `Se eliminarán también sus ${g.options.length} ${g.options.length === 1 ? 'opción' : 'opciones'}. Esta acción no se puede deshacer.`,
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try {
      await mod.deleteModifierGroup(g.id)
      toast('Grupo eliminado', 'success')
      navigate('/catalog/modifiers')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo eliminar el grupo.', 'danger')
    }
  }

  // ── option create/edit/remove ───────────────────────────────────────────
  function openOptionCreate() { setOptionDialog({ ...BLANK_OPTION }) }
  function openOptionEdit(option: mod.ModifierOptionDto) {
    setOptionDialog({ id: option.id, label: option.label, priceDeltaCents: option.priceDeltaCents, sortOrder: option.sortOrder })
  }
  async function saveOption() {
    if (!g || !optionDialog || !optionDialog.label.trim()) return
    setOptionBusy(true)
    try {
      if (optionDialog.id === null) {
        await mod.addOption(g.id, { label: optionDialog.label.trim(), priceDeltaCents: optionDialog.priceDeltaCents, sortOrder: optionDialog.sortOrder })
        toast('Opción agregada', 'success')
      } else {
        await mod.updateOption(g.id, optionDialog.id, { label: optionDialog.label.trim(), priceDeltaCents: optionDialog.priceDeltaCents, sortOrder: optionDialog.sortOrder })
        toast('Opción actualizada', 'success')
      }
      setOptionDialog(null)
      groups.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo guardar la opción.', 'danger')
    } finally {
      setOptionBusy(false)
    }
  }
  async function removeOption(option: mod.ModifierOptionDto) {
    if (!g) return
    const ok = await confirm({
      title: `¿Eliminar la opción "${option.label}"?`,
      message: 'Esta acción no se puede deshacer.',
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try {
      await mod.removeOption(g.id, option.id)
      toast('Opción eliminada', 'success')
      groups.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo eliminar la opción.', 'danger')
    }
  }
  async function toggleOptionActive(option: mod.ModifierOptionDto) {
    if (!g) return
    setBusyId(option.id)
    try {
      await mod.updateOption(g.id, option.id, { active: !option.active })
      toast(option.active ? 'Opción desactivada' : 'Opción activada', 'success')
      groups.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo cambiar el estado.', 'danger')
    } finally { setBusyId(null) }
  }

  // ── product linking ─────────────────────────────────────────────────────
  function openLinking() {
    if (!g) return
    const initial = new Set<string>(g.productIds ?? mod.getLinkedProductIdsStub(g.id))
    setLinkedIds(initial)
    setProductSearch('')
    setLinking(true)
  }
  function toggleProduct(productId: string) {
    setLinkedIds((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }
  async function saveLinks() {
    if (!g) return
    setLinkBusy(true)
    try {
      const prev = new Set<string>(g.productIds ?? mod.getLinkedProductIdsStub(g.id))
      const toLink: string[] = []
      const toUnlink: string[] = []
      for (const pid of linkedIds) if (!prev.has(pid)) toLink.push(pid)
      for (const pid of prev) if (!linkedIds.has(pid)) toUnlink.push(pid)
      for (const pid of toLink) await mod.linkProduct(g.id, pid)
      for (const pid of toUnlink) await mod.unlinkProduct(g.id, pid)
      toast('Productos vinculados actualizados', 'success')
      setLinking(false)
      groups.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudieron guardar los vínculos.', 'danger')
    } finally {
      setLinkBusy(false)
    }
  }

  const visibleProducts = useMemo(() => {
    const all = products.data ?? []
    const q = productSearch.trim().toLowerCase()
    if (!q) return all
    return all.filter((p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
  }, [products.data, productSearch])

  const kpis = useMemo(() => {
    if (!g) return []
    const items: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'danger' | 'info' | 'neutral' }[] = [
      { label: 'Selección', value: `${g.minSelect}–${g.maxSelect}` },
      { label: 'Opciones', value: g.options.length },
    ]
    if (g.productIds) items.push({ label: 'Productos', value: g.productIds.length })
    return items
  }, [g])

  const linkedProductIds = g?.productIds ?? []

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/catalog/modifiers')} aria-label="Volver a modificadores"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Grupo de modificadores</h1><p className="adm-page__sub">Modificadores · ficha del grupo</p></div>
        </div>
      </div>

      {asyncState(groups.loading, groups.error, groups.reload)}

      {!groups.loading && !g && (
        <div className="adm-empty">Grupo no encontrado. <button className="adm-btn adm-btn--sm" onClick={() => navigate('/catalog/modifiers')}>Volver a la lista</button></div>
      )}

      {g && (
        <EntityDetailPage
          title={g.name}
          subtitle={`Selección ${g.minSelect}–${g.maxSelect} · ${g.options.length} opción${g.options.length === 1 ? '' : 'es'}`}
          pills={<>
            <StatusPill tone={lifecycleTone(g.active)} label={g.active ? 'Activo' : 'Inactivo'} />
            {g.isRequired && <StatusPill tone="warn" label="Obligatorio" />}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={<>
            <button className="adm-btn adm-btn--sm" onClick={openLinking}><Link2 size={15} /> Vincular productos</button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={toggleGroupActive} disabled={busyId === g.id} title={g.active ? 'Desactivar grupo' : 'Activar grupo'}><Power size={15} /></button>
            <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={removeGroup} aria-label="Eliminar grupo"><Trash2 size={15} /></button>
          </>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'opciones', label: 'Opciones', count: g.options.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del grupo de modificadores"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre">{g.name}</Field>
              <Field label="Estado"><StatusPill tone={lifecycleTone(g.active)} label={g.active ? 'Activo' : 'Inactivo'} /></Field>
              <Field label="Selección mínima"><span className="adm-mono">{g.minSelect}</span></Field>
              <Field label="Selección máxima"><span className="adm-mono">{g.maxSelect}</span></Field>
              <Field label="Obligatorio">{g.isRequired ? 'Sí — el cajero no puede saltarlo' : 'No'}</Field>
              <Field label="Opciones"><span className="adm-mono">{g.options.length}</span></Field>
              <Field label="Productos vinculados">
                {g.productIds == null
                  ? <span className="adm-muted">No disponible en esta vista</span>
                  : linkedProductIds.length === 0
                    ? <span className="adm-muted">Sin productos vinculados</span>
                    : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {linkedProductIds.map((pid) => <span key={pid} className="adm-badge">{productName(pid)}</span>)}
                      </div>
                    )}
              </Field>
            </div>
          )}

          {tab === 'opciones' && (
            <div className="adm-card adm-card--pad0">
              <div className="adm-list">
                {g.options.length === 0 && <div className="adm-empty" style={{ padding: 12 }}>Sin opciones — añade la primera abajo.</div>}
                {g.options.map((o) => (
                  <div key={o.id} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>{o.label}</span>
                        {!o.active && <StatusPill tone={lifecycleTone(false)} label="Inactiva" />}
                      </div>
                      <div className="adm-row__meta">
                        {o.priceDeltaCents === 0
                          ? 'sin costo extra'
                          : `${o.priceDeltaCents > 0 ? '+' : ''}${mxn(o.priceDeltaCents)}`}
                      </div>
                    </div>
                    <div className="adm-row__tail">
                      <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => toggleOptionActive(o)} disabled={busyId === o.id} aria-label={o.active ? 'Desactivar opción' : 'Activar opción'} title={o.active ? 'Desactivar opción' : 'Activar opción'}><Power size={15} /></button>
                      <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openOptionEdit(o)} aria-label="Editar opción"><Pencil size={15} /></button>
                      <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeOption(o)} aria-label="Eliminar opción"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: '8px 14px 12px', borderTop: '1px solid var(--pos-separator)' }}>
                <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openOptionCreate}><Plus size={14} /> Añadir opción</button>
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={g.id} />}
        </EntityDetailPage>
      )}

      {/* ── Group edit sheet (mismo form que la lista) ───────────────────── */}
      <SectionedSheet
        open={editing}
        title="Editar grupo"
        onClose={() => setEditing(false)}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(false)} disabled={groupBusy}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveGroup} disabled={!groupForm.name.trim() || groupBusy}>{groupBusy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        <FormSection title="Datos básicos">
          <label className="adm-field">
            <span className="adm-field__label">Nombre</span>
            <input className="adm-input" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="Extras / Término / Salsa…" autoFocus />
          </label>
        </FormSection>
        <FormSection title="Reglas de selección">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label className="adm-field">
              <span className="adm-field__label">Mínimo</span>
              <input className="adm-input" type="number" min={0} value={groupForm.minSelect} onChange={(e) => setGroupForm({ ...groupForm, minSelect: Math.max(0, Number(e.target.value) || 0) })} />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Máximo</span>
              <input className="adm-input" type="number" min={1} value={groupForm.maxSelect} onChange={(e) => setGroupForm({ ...groupForm, maxSelect: Math.max(1, Number(e.target.value) || 1) })} />
            </label>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={groupForm.isRequired} onChange={(e) => setGroupForm({ ...groupForm, isRequired: e.target.checked })} />
            Obligatorio (el cajero no puede saltarlo)
          </label>
        </FormSection>
      </SectionedSheet>

      {/* ── Option create/edit sheet (mismo form que la lista) ───────────── */}
      <SectionedSheet
        open={optionDialog != null}
        title={optionDialog && optionDialog.id === null ? 'Nueva opción' : 'Editar opción'}
        onClose={() => setOptionDialog(null)}
        footer={<>
          <button className="adm-btn" onClick={() => setOptionDialog(null)} disabled={optionBusy}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveOption} disabled={!optionDialog?.label.trim() || optionBusy}>{optionBusy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {optionDialog && (
          <>
            <FormSection title="Datos básicos">
              <label className="adm-field">
                <span className="adm-field__label">Etiqueta</span>
                <input className="adm-input" value={optionDialog.label} onChange={(e) => setOptionDialog({ ...optionDialog, label: e.target.value })} placeholder="Queso extra / Rojo / Sin cebolla…" autoFocus />
              </label>
              <label className="adm-field">
                <span className="adm-field__label">Δ Precio (MXN)</span>
                <input className="adm-input" inputMode="decimal" value={(optionDialog.priceDeltaCents / 100).toString()} onChange={(e) => setOptionDialog({ ...optionDialog, priceDeltaCents: parseMxnToCents(e.target.value) })} placeholder="0.00" />
              </label>
            </FormSection>
            <FormSection title="Orden">
              <label className="adm-field">
                <span className="adm-field__label">Posición en la lista</span>
                <input className="adm-input" type="number" min={0} value={optionDialog.sortOrder} onChange={(e) => setOptionDialog({ ...optionDialog, sortOrder: Math.max(0, Number(e.target.value) || 0) })} placeholder="0" />
              </label>
            </FormSection>
          </>
        )}
      </SectionedSheet>

      {/* ── Product link sheet (multi-select, mismo que la lista) ────────── */}
      <SectionedSheet
        open={linking}
        title={g ? `Productos vinculados a "${g.name}"` : ''}
        onClose={() => setLinking(false)}
        footer={<>
          <button className="adm-btn" onClick={() => setLinking(false)} disabled={linkBusy}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveLinks} disabled={linkBusy}>{linkBusy ? 'Guardando…' : `Guardar (${linkedIds.size})`}</button>
        </>}
      >
        <FormSection title="Productos">
          <label className="adm-field">
            <span className="adm-field__label" style={{ display: 'none' }}>Buscar producto</span>
            <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, color: 'var(--pos-fg-tertiary)' }} aria-hidden />
              <input className="adm-input" placeholder="Buscar producto por nombre o SKU…" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} style={{ paddingLeft: 30 }} />
            </span>
          </label>
          {asyncState(products.loading, products.error, products.reload)}
          {products.data && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 340, overflowY: 'auto' }}>
              {visibleProducts.length === 0 && <div className="adm-empty">Sin productos</div>}
              {visibleProducts.map((p: Product) => {
                const checked = linkedIds.has(p.id)
                return (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: checked ? 'var(--pos-elevated)' : 'transparent' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 500 }}>{p.name}</span>
                      <span className="adm-row__meta">{p.sku} · {mxn(p.priceCents)}</span>
                    </span>
                  </label>
                )
              })}
            </div>
          )}
        </FormSection>
      </SectionedSheet>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
